import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { formatApiError } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setError("Please accept the Terms & Conditions to create an account.");
      return;
    }
    if (marketingConsent && !phone.trim()) {
      setError("Please add a phone number to opt in to text marketing.");
      return;
    }
    setLoading(true); setError("");
    try {
      await register({
        email: email.trim().toLowerCase(),
        password,
        name,
        phone,
        terms_accepted: termsAccepted,
        email_marketing_opt_in: marketingConsent,
        sms_marketing_opt_in: marketingConsent,
      });
      nav("/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="register-form">
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Create account</h1>
            <p className="text-sm text-neutral-500 mt-1">Already registered? <Link to="/login" className="text-[hsl(var(--primary))] font-medium">Sign in</Link></p>
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="register-error">{error}</div>}
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name"/>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email"/>
          </div>
          <div>
            <Label htmlFor="phone">Phone (optional)</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 555-5555"
              data-testid="register-phone"
            />
          </div>
          <div>
            <Label htmlFor="pw">Password</Label>
            <div className="relative">
              <Input
                id="pw"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="pr-11"
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-neutral-500 transition-colors hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
                data-testid="register-password-toggle"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <fieldset className="space-y-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <legend className="px-1 text-sm font-semibold text-neutral-900">
              Marketing & Promotional Communications
            </legend>
            <label className="flex items-start gap-3 text-sm leading-relaxed text-neutral-700">
              <Checkbox
                checked={marketingConsent}
                onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                data-testid="register-marketing-consent"
              />
              <span>
                I agree to receive promotional emails and automated text messages from Sandar Sushi,
                including offers, discounts, updates, and reminders. Consent is optional and not
                required for purchase. Message frequency varies; message and data rates may apply.
                Unsubscribe anytime or text STOP to opt out. {" "}
                <Link to="/privacy-policy" className="font-medium text-[hsl(var(--primary))] underline">
                  Privacy Policy
                </Link>{" "}
                | {" "}
                <Link to="/terms" className="font-medium text-[hsl(var(--primary))] underline">
                  Terms & Conditions
                </Link>
              </span>
            </label>
          </fieldset>
          <label className="flex items-start gap-3 text-sm leading-relaxed text-neutral-700">
            <Checkbox
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(checked === true)}
              data-testid="register-terms-accepted"
            />
            <span>
              I agree to the <Link to="/terms" className="font-medium text-[hsl(var(--primary))] underline">Terms & Conditions</Link>{" "}
              and <Link to="/privacy-policy" className="font-medium text-[hsl(var(--primary))] underline">Privacy Policy</Link>.
            </span>
          </label>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="register-submit">
            {loading ? "Creating…" : "Create Account"}
          </Button>
        </form>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://customer-assets-0z36b82j.emergentagent.net/job_sandar-eats/artifacts/v4xitkzq_Sandar%20Sushi%20Store.PNG" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-black/20"/>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="eyebrow text-white/70 mb-3">Join Sandar</p>
          <h2 className="font-display font-bold text-4xl leading-tight">Order faster.<br/>Good food, brighter days.</h2>
        </div>
      </div>
    </div>
  );
}
